import { createContext, useContext, ReactNode } from 'react';
import { useCountdown } from '../hooks/useCountdown';
import { usePowerBreak } from '../hooks/usePowerBreak';
import { usePreEventCountdown } from '../hooks/usePreEventCountdown';
import { useAgenda } from './AgendaContext';

interface TimerContextValue {
  timeRemaining: number;
  isPowerBreakActive: boolean;
  powerBreakTimeRemaining: number;
  startPowerBreak: () => Promise<void>;
  cancelPowerBreak: () => Promise<void>;
  isPreEvent: boolean;
  preEventTimeRemaining: number;
}

export const TimerContext = createContext<TimerContextValue | undefined>(undefined);

interface TimerProviderProps {
  children: ReactNode;
}

export function TimerProvider({ children }: TimerProviderProps) {
  const { currentItem, refreshCurrentItem } = useAgenda();
  
  // Use useCountdown hook for agenda timer with callback to refresh current item
  const timeRemaining = useCountdown(currentItem?.endTime || null, refreshCurrentItem);
  
  // Use usePowerBreak hook for Power Break timer
  const {
    isActive: isPowerBreakActive,
    timeRemaining: powerBreakTimeRemaining,
    start: startPowerBreak,
    cancel: cancelPowerBreak,
  } = usePowerBreak();
  
  // Use usePreEventCountdown hook for pre-event state
  const { isPreEvent, days, hours, minutes, seconds } = usePreEventCountdown();
  
  // Calculate total seconds remaining for pre-event countdown
  const preEventTimeRemaining = isPreEvent
    ? days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds
    : 0;

  const value: TimerContextValue = {
    timeRemaining,
    isPowerBreakActive,
    powerBreakTimeRemaining,
    startPowerBreak,
    cancelPowerBreak,
    isPreEvent,
    preEventTimeRemaining,
  };

  return (
    <TimerContext.Provider value={value}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
}
